import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { GLOBAL_ASK_PROMPTS } from '../utils/globalAskCaptainCook.js'

const LAUNCHER_INPUT_PLACEHOLDER = 'What can I make with chicken, lemon & broccoli?'

/**
 * Lightweight Ask the Cook launcher — suggestion chips + search field only.
 * Submitting a query calls onSubmit; the full chat overlay is opened by the parent.
 *
 * variant:
 *   dropdown — anchored below the nav field (desktop)
 *   sheet    — bottom sheet above the tab bar (mobile FAB / mobile nav)
 */
export default function AskTheCookLauncher({
  open = false,
  onClose,
  onSubmit,
  anchorRef,
  variant = 'dropdown',
}) {
  const [query, setQuery] = useState('')
  const [position, setPosition] = useState({ top: 0, left: 0, width: 340 })
  const panelRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!open) {
      setQuery('')
      return undefined
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open || variant !== 'dropdown') return undefined

    function updatePosition() {
      const anchor = anchorRef?.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const width = Math.max(rect.width, 360)
      const left = Math.min(rect.left, window.innerWidth - width - 16)
      setPosition({
        top: rect.bottom + 8,
        left: Math.max(16, left),
        width,
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open, variant, anchorRef])

  useEffect(() => {
    if (!open) return undefined

    function handlePointerDown(event) {
      const target = event.target
      if (panelRef.current?.contains(target)) return
      if (anchorRef?.current?.contains(target)) return
      onClose?.()
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose, anchorRef])

  function submitValue(value) {
    const trimmed = String(value || '').trim()
    if (!trimmed) return
    onSubmit?.(trimmed)
  }

  function handleFormSubmit(event) {
    event.preventDefault()
    submitValue(query)
  }

  if (!open || typeof document === 'undefined') return null

  const panel = (
    <div
      ref={panelRef}
      id="ask-the-cook-launcher"
      role="dialog"
      aria-label="Ask the Cook suggestions"
      className={
        variant === 'sheet'
          ? 'ask-the-cook-launcher ask-the-cook-launcher--sheet'
          : 'ask-the-cook-launcher ask-the-cook-launcher--dropdown'
      }
      style={
        variant === 'dropdown'
          ? {
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
            }
          : undefined
      }
      onMouseDown={(e) => e.stopPropagation()}
    >
      <form className="ask-the-cook-launcher-form" onSubmit={handleFormSubmit}>
        <div className="composer-pill composer-pill--ai">
          <span className="composer-pill__sparkle" aria-hidden="true">✦</span>
          <input
            ref={inputRef}
            type="search"
            className="composer-pill__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={LAUNCHER_INPUT_PLACEHOLDER}
            aria-label="Ask the Cook"
            data-testid="ask-launcher-input"
            autoComplete="off"
          />
          <button
            type="submit"
            className="composer-pill__submit composer-pill__submit--ai"
            disabled={!query.trim()}
            aria-label="Ask"
            data-testid="ask-launcher-submit"
          >
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </form>
      <div className="ask-the-cook-launcher-suggestions">
        <div className="suggestion-chips-label">Try</div>
        <div className="suggestion-chips-list suggestion-chips-list--stack">
          {GLOBAL_ASK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className="suggestion-chip"
              onClick={() => submitValue(prompt)}
              data-testid="ask-launcher-suggestion"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  if (variant === 'sheet') {
    return createPortal(
      <>
        <div
          className="ask-the-cook-launcher-backdrop"
          aria-hidden="true"
          onClick={() => onClose?.()}
        />
        {panel}
      </>,
      document.body
    )
  }

  return createPortal(panel, document.body)
}
